// Copyright 2021-2022 zcloak authors & contributors
// SPDX-License-Identifier: Apache-2.0

import {
  Box,
  Button,
  Container as MuiContainer,
  Dialog,
  DialogActions,
  DialogContent,
  GlobalStyles,
  lighten,
  Paper,
  Stack,
  styled,
  Typography
} from '@mui/material';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Countdown from 'react-countdown';

import { VerifiablePresentation } from '@zcloak/vc/types';

import DialogHeader from '../../components/DialogHeader';
import { endpoint } from '../../config';
import { usePhase } from '../../hooks/usePhase';
import { useToggle } from '../../hooks/useToggle';
import { ZkidWalletContext } from '../../ZkidWallet';
import IconFail from '../verify/icon_fail.svg';
import IconSuccess from '../verify/icon_success.svg';
import DateTitle from './DateTitle';
import EthInput from './EthInput';
import Partner from './Partner';

const Wrapper = styled(Box)({
  paddingTop: 128,
  paddingBottom: 128
});

const Container = styled(MuiContainer)({
  margin: '0 auto'
});

const Content = styled(Paper)({
  marginTop: 24,
  padding: '20px 44px',
  background: 'rgba(255,255,255,0.8)',
  boxShadow: '0px 3px 30px rgba(27,145,150,0.3)',
  borderRadius: 8
});

function Prize() {
  const phases = usePhase();
  const [open, toggleOpen] = useToggle();

  const { didUrl, requestDigestVp } = useContext(ZkidWalletContext);
  const [status, setStatus] = useState<{ success: boolean; message: string }>();
  const end = useMemo(() => Date.now() > 1671926400000, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const requestBindAddress = async (data: { address: string; vp: VerifiablePresentation }) => {
    const res = await fetch(`${endpoint}/bind_address`, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (res.ok) {
      return res.json();
    }
  };

  const submitEth = useCallback(
    (ethAddr?: string) => {
      if (!ethAddr && ethAddr?.length !== 42) return;
      didUrl &&
        requestDigestVp().then((res) => {
          requestBindAddress({
            address: ethAddr,
            vp: res
          }).then((_r) => {
            if (_r.status === 0) {
              setStatus({ success: true, message: 'Bind successfully.' });
            } else {
              setStatus({ success: false, message: _r.message });
            }

            toggleOpen();
          });
        });
    },
    [didUrl, requestDigestVp, toggleOpen]
  );

  return (
    <Wrapper>
      <GlobalStyles
        styles={{
          body: {
            background: 'url(png_bac.svg) no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }
        }}
      />
      <Container>
        <Typography fontWeight={500} mb={2.5} textAlign="center" variant="h2">
          Claim Your Prizes
        </Typography>
        <Box
          component={Content}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <DateTitle disable={end} end="Dec.24" start="Dec.21" />
              <Typography ml={2} variant="h5">
                Submit your ETH address to join the whitelist：
              </Typography>
            </Box>
            <Box>
              <EthInput
                disable={end || phases?.[0] === false}
                submitEth={submitEth}
                text={
                  phases?.[0] === false
                    ? 'Sorry, you did not share this VC in the right way in Phase I'
                    : end
                    ? 'Sorry, whitelist addresses are no longer collected'
                    : ''
                }
              />
            </Box>
          </Box>

          <Box>
            <Typography mb={2}>End Countdown: </Typography>
            <Countdown
              date={1671926400000}
              renderer={({ completed, days, hours, minutes, seconds }) => {
                if (completed) {
                  return 'End';
                } else {
                  return (
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        '>div': {
                          width: '48px',
                          height: '48px',
                          background: '#333333',
                          border: '1px solid #707070',
                          borderRadius: '4px',
                          fontSize: '34px',
                          lineHeight: '48px',
                          textAlign: 'center',
                          fontWeight: 700,
                          color: '#E2B436'
                        }
                      }}
                    >
                      <Box>{days}D</Box>
                      <Box>{String(hours).length === 1 ? `0${hours}` : hours}</Box>
                      <Box>{String(minutes).length === 1 ? `0${minutes}` : minutes}</Box>
                      <Box>{String(seconds).length === 1 ? `0${seconds}` : seconds}</Box>
                    </Stack>
                  );
                }
              }}
            />
          </Box>
        </Box>

        <Content>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DateTitle disable={!end} end="Future" start="Dec.25" />
            <Typography ml={2} variant="h5">
              Claim Your Prizes
            </Typography>
          </Box>
          <Typography mt={2} variant="h6">
            For Winners 🏆{' '}
          </Typography>
          <Box display="flex">
            <Typography color="#1B9196" fontSize="18px" mt={2}>
              BUSD from{' '}
              <span style={{ fontWeight: 700, textDecoration: 'underline' }}>zCloak Network</span>
            </Typography>
            <Typography fontSize="18px" ml={2} mt={2}>
              The prize will be sent to the winner’s address on 26 December.
            </Typography>
          </Box>
          <Box alignItems="center" display="flex" mt={2}>
            <Typography color="#1B9196" fontSize="18px" mr={2}>
              Player Pass NFT Whitelist from{' '}
              <span style={{ fontWeight: 700, textDecoration: 'underline' }}>Macondo</span>
            </Typography>
            <Button
              onClick={() => {
                window.open('https://discord.gg/macondo');
              }}
              variant="contained"
            >
              Join Discord
            </Button>
            <Typography color="#1B9196" fontSize="18px" ml={2} mr={2}>
              {"to contact Macondo in 'technical support' channel"}
            </Typography>
          </Box>
          <Typography mb={2} mt={3} variant="h6">
            For Every Participant 🎁
          </Typography>
          <Partner />
          <Typography fontSize="18px" mb={5} mt={6} textAlign="center">
            Prize pool certified by{' '}
            <span style={{ fontWeight: 700, textDecoration: 'underline' }}>LegalDAO</span>
          </Typography>
        </Content>
        {open && (
          <Dialog
            PaperProps={{
              sx: {
                background: status
                  ? status.success
                    ? `linear-gradient(330deg, ${lighten('#FFEDB7', 0.6)} 0%, ${lighten(
                        '#FFA6CB',
                        0.6
                      )} 100%)`
                    : `linear-gradient(180deg, ${lighten(
                        '#F5A5AF',
                        0.4
                      )} 0%, rgb(255,255,255) 100%)`
                  : undefined
              }
            }}
            maxWidth="sm"
            onClose={toggleOpen}
            open={open}
          >
            <DialogHeader onClose={toggleOpen}></DialogHeader>
            <DialogContent sx={{ minWidth: 600 }}>
              {status && status.success ? (
                <Stack alignItems="center" my={7} spacing={3}>
                  <img src={IconSuccess} />
                  <Typography textAlign="center" variant="h5">
                    {status.message}
                  </Typography>
                </Stack>
              ) : (
                <Stack alignItems="center" my={7} spacing={3}>
                  <img src={IconFail} />
                  <Typography textAlign="center" variant="h5">
                    {status?.message}
                  </Typography>
                </Stack>
              )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center' }}>
              <Button onClick={toggleOpen} size="large" sx={{ width: '50%' }} variant="contained">
                Finish
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Container>
    </Wrapper>
  );
}

export default Prize;
